import { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "../../lib/utils";

type PageHeaderProps = {
  eyebrow?: (string) | undefined;
  title: string;
  description: string;
  icon?: (LucideIcon) | undefined;
  backAction?: (ReactNode) | undefined;
  actions?: (ReactNode) | undefined;
  className?: (string) | undefined;
};

export function PageHeader({
  actions,
  backAction,
  className,
  description,
  eyebrow,
  icon: Icon,
  title,
}: PageHeaderProps) {
  return (
    <header
      className={cn(
        "relative isolate min-w-0 overflow-hidden rounded-[16px] border border-blue-300/20 bg-[linear-gradient(100deg,#071a3a_0%,#123d82_58%,#2563eb_100%)] px-4 py-2.5 text-white shadow-[0_12px_30px_rgba(15,23,42,0.14)] sm:min-h-[72px] sm:px-5 sm:py-3",
        className,
      )}
    >
      <div className="pointer-events-none absolute -right-12 -top-20 -z-10 size-56 rounded-full bg-white/[0.08] blur-2xl" aria-hidden="true" />
      <div className="pointer-events-none absolute -bottom-24 left-1/3 -z-10 h-44 w-80 rounded-full bg-cyan-300/[0.08] blur-3xl" aria-hidden="true" />

      <div className="flex min-w-0 flex-col justify-center gap-3 lg:flex-row lg:items-center lg:justify-between lg:gap-4">
        <div className="flex min-w-0 items-center gap-3">
          {backAction ? <div className="shrink-0">{backAction}</div> : null}
          {Icon ? (
            <div className="grid size-10 shrink-0 place-items-center rounded-[11px] border border-white/20 bg-white/[0.12] text-cyan-100 shadow-sm backdrop-blur-sm sm:size-11">
              <Icon className="size-5 sm:size-[22px]" aria-hidden="true" />
            </div>
          ) : null}
          <div className="min-w-0 max-w-3xl">
            {eyebrow ? (
              <p className="mb-0.5 text-[10px] font-bold uppercase leading-3 tracking-[0.15em] text-blue-100/85">
                {eyebrow}
              </p>
            ) : null}
            <h1 className="truncate text-[21px] font-bold leading-6 tracking-tight text-white sm:text-[23px]">{title}</h1>
            <p className="mt-0.5 truncate text-xs font-medium leading-[18px] text-blue-50/85 sm:text-[13px]">{description}</p>
          </div>
        </div>
        {actions ? (
          <div className="flex w-full min-w-0 shrink-0 flex-wrap items-center gap-2 lg:w-auto lg:max-w-[46%] lg:flex-nowrap lg:justify-end">
            {actions}
          </div>
        ) : null}
      </div>
    </header>
  );
}

export function PageHeaderChip({ icon: Icon, label }: { icon?: (LucideIcon) | undefined; label: string }) {
  return (
    <div className="inline-flex min-h-9 max-w-full items-center gap-2 rounded-full border border-white/20 bg-white/[0.12] px-3 py-1.5 text-xs font-semibold text-white shadow-sm backdrop-blur-sm">
      {Icon ? <Icon className="size-3.5 shrink-0 text-cyan-100" aria-hidden="true" /> : null}
      <span className="min-w-0 truncate">{label}</span>
    </div>
  );
}
